import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Search, Link2, Archive, Pencil, KeyRound, RotateCcw, Trash2, Users, GitMerge, ArrowRightLeft, Download, IdCard, Send, Printer } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Avatar, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ImportWorkbook } from "@/components/ImportWorkbook";

const TABS = ["dancers", "teachers", "parents", "families", "accounts"];

export default function People() {
  const { user } = useAuth();
  const isDirector = user?.role === "director";
  const [tab, setTab] = useState("dancers");
  const [dancers, setDancers] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [parents, setParents] = useState([]);
  const [families, setFamilies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [classes, setClasses] = useState([]);
  const [logins, setLogins] = useState({});
  const [q, setQ] = useState("");
  const [statusView, setStatusView] = useState("active");

  const load = () => {
    api.get("/people/dancers").then((r) => setDancers(r.data));
    api.get("/people/teachers").then((r) => setTeachers(r.data));
    api.get("/people/parents").then((r) => setParents(r.data));
    api.get("/people/accounts").then((r) => setAccounts(r.data)).catch(() => {});
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
    api.get("/people/dancer-logins").then((r) => setLogins(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const flt = (list) => list.filter((p) => {
    const hay = `${p.first_name || p.name || ""} ${p.last_name || ""} ${p.email || ""} ${p.phone || ""}`.toLowerCase();
    const okQ = hay.includes(q.toLowerCase());
    const active = p.active !== false;
    const okS = statusView === "all" ? true : statusView === "inactive" ? !active : active;
    return okQ && okS;
  });

  return (
    <div className="stagger-in">
      <PageHeader title="People" subtitle="Manage teachers, dancers, parents and families." testid="people-page" />

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="people-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…" className="pl-9 rounded-full bg-white" />
        </div>
        <label className="flex items-center gap-2 text-sm font-medium">
          <span className="flex bg-white border rounded-full p-1" data-testid="status-filter">
            {["active", "inactive", "all"].map((s) => (
              <button key={s} data-testid={`status-${s}`} onClick={() => setStatusView(s)} className={`px-4 h-9 rounded-full text-sm font-semibold capitalize transition-colors ${statusView === s ? "bg-teal text-white" : "text-muted-foreground"}`}>{s}</button>
            ))}
          </span>
        </label>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white rounded-full p-1 mb-6 flex-wrap h-auto">
          {TABS.map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`people-tab-${t}`} className="rounded-full capitalize data-[state=active]:bg-teal data-[state=active]:text-white">{t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="dancers"><DancerSection dancers={flt(dancers)} parents={parents} allDancers={dancers} classes={classes} logins={logins} isDirector={isDirector} onChange={load} /></TabsContent>
        <TabsContent value="teachers"><TeacherSection teachers={flt(teachers)} onChange={load} /></TabsContent>
        <TabsContent value="parents"><ParentSection parents={flt(parents)} dancers={dancers} isDirector={isDirector} onChange={load} /></TabsContent>
        <TabsContent value="families"><FamilyView dancers={dancers} parents={parents} onChange={load} /></TabsContent>
        <TabsContent value="accounts"><AccountSection accounts={accounts} dancers={dancers} teachers={teachers} parents={parents} onChange={load} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-sm font-semibold">{label}</Label>{children}</div>;
}

/* ---------------- Dancers ---------------- */
function DancerSection({ dancers, parents, allDancers, classes, logins, isDirector, onChange }) {
  const empty = { first_name: "", last_name: "", preferred_name: "", dob: "", grade: "", email: "", start_date: "", active: true, parent_ids: [], medical_alert: "", nhsda_member: "", director_notes: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [classIds, setClassIds] = useState([]);
  const [classQ, setClassQ] = useState("");
  const [pQ, setPQ] = useState("");
  const [addingParent, setAddingParent] = useState(false);
  const [newParent, setNewParent] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [selected, setSelected] = useState([]);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassign, setReassign] = useState({ to_class_ids: [], mode: "copy", from_class_id: "" });
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeKeep, setMergeKeep] = useState("");
  const [busy, setBusy] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportClass, setExportClass] = useState("");

  const undo = async (uid) => {
    if (!uid) return;
    try { await api.post(`/people/undo/${uid}`); toast.success("Reverted"); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not undo"); }
  };
  const doExport = async () => {
    try {
      const r = await api.get("/people/export/roster", { params: exportClass ? { class_id: exportClass } : {}, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `studiov_roster_${exportClass || "all"}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Roster exported"); setExportOpen(false);
    } catch { toast.error("Export failed"); }
  };

  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const clearSel = () => setSelected([]);
  const selDancers = dancers.filter((d) => selected.includes(d.id));
  const toggleReClass = (cid) => setReassign((r) => ({ ...r, to_class_ids: r.to_class_ids.includes(cid) ? r.to_class_ids.filter((x) => x !== cid) : [...r.to_class_ids, cid] }));
  const openReassign = () => { setReassign({ to_class_ids: [], mode: "copy", from_class_id: "" }); setReassignOpen(true); };
  const doReassign = async () => {
    if (reassign.to_class_ids.length === 0 && reassign.mode === "copy") { toast.error("Pick at least one class to copy into"); return; }
    if (reassign.mode === "move" && !reassign.from_class_id) { toast.error("Choose the class to move dancers out of"); return; }
    setBusy(true);
    try {
      const r = await api.post("/people/dancers/bulk-reassign", { dancer_ids: selected, ...reassign });
      const uid = r.data.undo_id;
      toast.success(`${r.data.enrollments_added} added, ${r.data.enrollments_removed} removed`, uid ? { action: { label: "Undo", onClick: () => undo(uid) }, duration: 12000 } : undefined);
      setReassignOpen(false); clearSel(); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };
  const openMerge = () => { setMergeKeep(selected[0] || ""); setMergeOpen(true); };
  const doMerge = async () => {
    const keep = mergeKeep; const remove = selected.find((x) => x !== keep);
    if (!keep || !remove) { toast.error("Select exactly two dancers"); return; }
    setBusy(true);
    try {
      const rm = await api.post("/people/dancers/merge", { keep_id: keep, remove_id: remove });
      const uid = rm.data.undo_id;
      toast.success("Dancers merged", uid ? { action: { label: "Undo", onClick: () => undo(uid) }, duration: 12000 } : undefined);
      setMergeOpen(false); clearSel(); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  const nameOf = (id) => { const d = (allDancers || []).find((x) => x.id === id); return d ? `${d.first_name} ${d.last_name}` : ""; };
  const classOf = (id) => { const c = classes.find((x) => x.id === id); return c ? c.name : "class"; };
  const parentOf = (id) => parents.find((x) => x.id === id);

  const openAdd = () => { setForm(empty); setEditId(null); setClassIds([]); setClassQ(""); setPQ(""); setAddingParent(false); setOpen(true); };
  const openEdit = async (d) => {
    setForm({ ...empty, ...d }); setEditId(d.id); setClassQ(""); setPQ(""); setAddingParent(false); setOpen(true);
    try { const r = await api.get(`/enrollments?dancer_id=${d.id}`); setClassIds([...new Set(r.data.filter((e) => e.status !== "Withdrawn").map((e) => e.class_id))]); }
    catch { setClassIds([]); }
  };
  const save = async () => {
    if (!form.first_name.trim()) { toast.error("First name required"); return; }
    try {
      let id = editId;
      if (editId) await api.put(`/people/dancers/${editId}`, form);
      else { const r = await api.post("/people/dancers", form); id = r.data.id; }
      await api.post(`/people/dancers/${id}/set-classes`, { class_ids: classIds });
      toast.success("Dancer saved"); setOpen(false); setForm(empty); setEditId(null); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const archive = async (id) => { await api.post(`/people/dancers/${id}/archive`); toast.success("Marked inactive"); onChange(); };
  const reactivate = async (id) => { await api.post(`/people/dancers/${id}/reactivate`); toast.success("Reactivated"); onChange(); };
  const [delTarget, setDelTarget] = useState(null);
  const [impact, setImpact] = useState(null);
  const askDelete = async (d) => { setDelTarget(d); setImpact(null); try { const r = await api.get(`/people/dancers/${d.id}/impact`); setImpact(r.data); } catch { /* ignore */ } };
  const doDelete = async () => { try { await api.delete(`/people/dancers/${delTarget.id}`); toast.success("Dancer deleted"); setDelTarget(null); onChange(); } catch { toast.error("Error"); } };

  const toggleClass = (cid) => setClassIds((ids) => ids.includes(cid) ? ids.filter((x) => x !== cid) : [...ids, cid]);
  const toggleParent = (pid) => setForm((f) => ({ ...f, parent_ids: f.parent_ids?.includes(pid) ? f.parent_ids.filter((x) => x !== pid) : [...(f.parent_ids || []), pid] }));
  const addParentInline = async () => {
    if (!newParent.first_name.trim()) { toast.error("Parent first name required"); return; }
    try { const r = await api.post("/people/parents", newParent); setForm((f) => ({ ...f, parent_ids: [...(f.parent_ids || []), r.data.id] })); setAddingParent(false); setNewParent({ first_name: "", last_name: "", email: "", phone: "" }); toast.success("Parent added & linked"); onChange(); }
    catch { toast.error("Error adding parent"); }
  };

  const [loginDlg, setLoginDlg] = useState(false);
  const [loginForm, setLoginForm] = useState({ dancer: null, username: "", pin: "" });
  const openLogin = (d) => { setLoginForm({ dancer: d, username: "", pin: "" }); setLoginDlg(true); };
  const saveLogin = async () => {
    try {
      const res = await api.post(`/people/dancers/${loginForm.dancer.id}/login`, { username: loginForm.username, pin: loginForm.pin });
      toast.success(res.data.updated ? "Dancer login updated" : `Login created: ${res.data.username}`);
      setLoginDlg(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const [accessDlg, setAccessDlg] = useState(null);
  const [setupRecipient, setSetupRecipient] = useState("dancer");
  const [setupParent, setSetupParent] = useState("");
  const [cardData, setCardData] = useState(null);
  const openAccess = (d) => { setAccessDlg(d); setSetupRecipient(d.email ? "dancer" : "parent"); setSetupParent((d.parent_ids || [])[0] || ""); };
  const genCard = async (d) => {
    try { const r = await api.post(`/people/dancers/${d.id}/login-card`); setAccessDlg(null); setCardData({ cards: [r.data] }); toast.success("Login card ready — PIN shown once"); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const sendSetup = async (d) => {
    try { const r = await api.post(`/people/dancers/${d.id}/send-setup`, { recipient: setupRecipient, parent_id: setupParent }); toast.success(`Setup link sent to ${r.data.to}`); setAccessDlg(null); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const [cardsDlg, setCardsDlg] = useState({ open: false, scope: "selected", classId: "", items: [], replace: [], cards: null, hub: "" });
  const previewCards = async (scope, classId) => {
    if (scope === "class" && !classId) { setCardsDlg((v) => ({ ...v, items: [], cards: null })); return; }
    const body = scope === "selected" ? { dancer_ids: selected } : scope === "class" ? { class_id: classId } : { all_active: true };
    try { const r = await api.post("/people/dancers/login-cards/preview", body); setCardsDlg((v) => ({ ...v, items: r.data.items, hub: r.data.hub_url, replace: [], cards: null })); }
    catch { toast.error("Could not load preview"); }
  };
  const openCards = (scope) => { const s = scope || (selected.length ? "selected" : "all"); setCardsDlg({ open: true, scope: s, classId: "", items: [], replace: [], cards: null, hub: "" }); previewCards(s, ""); };
  const cardsScope = (s) => { setCardsDlg((v) => ({ ...v, scope: s, classId: "", cards: null })); previewCards(s, ""); };
  const cardsClass = (cid) => { setCardsDlg((v) => ({ ...v, classId: cid, cards: null })); previewCards("class", cid); };
  const toggleReplace = (did) => setCardsDlg((v) => ({ ...v, replace: v.replace.includes(did) ? v.replace.filter((x) => x !== did) : [...v.replace, did] }));
  const generateCards = async () => {
    const ids = cardsDlg.items.map((i) => i.dancer_id);
    if (!ids.length) { toast.error("No dancers selected"); return; }
    try { const r = await api.post("/people/dancers/login-cards", { dancer_ids: ids, replace_ids: cardsDlg.replace }); setCardsDlg((v) => ({ ...v, cards: r.data.cards, hub: r.data.hub_url })); toast.success(`${r.data.cards.length} card(s) ready`); onChange(); }
    catch { toast.error("Could not generate cards"); }
  };
  const downloadCardsPdf = async () => {
    try {
      const r = await api.post("/people/dancers/login-cards/pdf", { cards: cardsDlg.cards.map((c) => ({ name: c.name, username: c.username, pin: c.pin })), hub_url: cardsDlg.hub }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = "dancer_login_cards.pdf"; a.click(); URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("Could not build PDF"); }
  };
  const bulkSetup = async () => {
    try { const r = await api.post("/people/dancers/send-setup-bulk", { dancer_ids: selected }); toast.success(`Sent ${r.data.sent}${r.data.skipped.length ? `, skipped ${r.data.skipped.length}` : ""}`); if (r.data.skipped.length) toast.message("No email on file for", { description: r.data.skipped.join(", ") }); clearSel(); onChange(); }
    catch { toast.error("Error"); }
  };
  const printCards = (cards) => {
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const hub = (process.env.REACT_APP_BACKEND_URL || "").replace(/^https?:\/\//, "").replace(/\/$/, "");
    const body = cards.map((c) => `<div class="card"><div class="brand">Studio V — Dancer Login</div><div class="nm">${esc(c.name)}</div><div class="row"><span>Username</span><b>${esc(c.username)}</b></div>${c.pin ? `<div class="row"><span>Temporary PIN</span><b class="pin">${esc(c.pin)}</b></div>` : `<div class="row"><span>PIN</span><b>Use existing</b></div>`}${hub ? `<div class="row"><span>Sign in at</span><b>${esc(hub)}</b></div>` : ""}<div class="note">${c.pin ? `You'll choose your own PIN on first sign-in. This temporary PIN expires in ${esc(c.expires_days || 14)} days.` : "Sign in with your existing PIN."}</div></div>`).join("");
    const w = window.open("", "_blank");
    if (!w) { toast.error("Enable pop-ups to print"); return; }
    w.document.write(`<html><head><title>Login Cards</title><style>@page{margin:12mm}body{font-family:Arial,sans-serif}.grid{display:flex;flex-wrap:wrap;gap:8mm}.card{width:85mm;border:1px dashed #94a3b8;border-radius:8px;padding:7mm;box-sizing:border-box}.brand{color:#0d9488;font-weight:bold;font-size:11px;text-transform:uppercase;letter-spacing:1px}.nm{font-size:18px;font-weight:bold;margin:6px 0}.row{display:flex;justify-content:space-between;border-top:1px solid #eee;padding:5px 0;font-size:14px}.pin{letter-spacing:3px}.note{color:#64748b;font-size:10px;margin-top:8px}</style></head><body><div class="grid">${body}</div><script>window.onload=function(){window.print()}</script></body></html>`);
    w.document.close();
  };

  const classMatches = classes.filter((c) => c.active !== false && (!classQ || (c.name || "").toLowerCase().includes(classQ.toLowerCase())) && !classIds.includes(c.id)).slice(0, 8);
  const parentMatches = parents.filter((p) => { if (form.parent_ids?.includes(p.id)) return false; const hay = `${p.first_name} ${p.last_name} ${p.email || ""} ${p.phone || ""}`.toLowerCase(); return pQ.trim() && hay.includes(pQ.toLowerCase()); }).slice(0, 8);

  return (
    <div>
      <div className="flex justify-end gap-2 mb-4">
        {isDirector && <ImportWorkbook onDone={onChange} />}
        {isDirector && <Button data-testid="export-roster-button" variant="outline" className="rounded-full" onClick={() => { setExportClass(""); setExportOpen(true); }}><Download className="w-4 h-4 mr-2" />Export roster</Button>}
        {isDirector && <Button data-testid="print-login-cards-button" variant="outline" className="rounded-full" onClick={() => openCards()}><IdCard className="w-4 h-4 mr-2" />Print login cards</Button>}
        <Button data-testid="add-dancer-button" onClick={openAdd} className="rounded-full bg-teal hover:bg-teal-dark">
          <UserPlus className="w-4 h-4 mr-2" />Add Dancer
        </Button>
      </div>
      {isDirector && selected.length > 0 && (
        <div data-testid="dancer-bulk-bar" className="flex flex-wrap items-center gap-2 mb-4 bg-teal-light border border-teal/30 rounded-2xl px-4 py-3">
          <span className="text-sm font-semibold text-teal-dark">{selected.length} selected</span>
          <Button size="sm" data-testid="bulk-reassign-button" className="rounded-full bg-teal hover:bg-teal-dark ml-2" onClick={openReassign}><ArrowRightLeft className="w-3.5 h-3.5 mr-1" />Reassign classes</Button>
          <Button size="sm" variant="outline" data-testid="bulk-merge-button" disabled={selected.length !== 2} className="rounded-full" onClick={openMerge}><GitMerge className="w-3.5 h-3.5 mr-1" />Merge duplicates</Button>
          <Button size="sm" variant="outline" data-testid="bulk-cards-button" className="rounded-full" onClick={() => openCards("selected")}><IdCard className="w-3.5 h-3.5 mr-1" />Login cards</Button>
          <Button size="sm" variant="outline" data-testid="bulk-setup-button" className="rounded-full" onClick={bulkSetup}><Send className="w-3.5 h-3.5 mr-1" />Send setup links</Button>
          {selected.length !== 2 && <span className="text-xs text-muted-foreground">Select exactly 2 to merge</span>}
          <Button size="sm" variant="ghost" className="rounded-full ml-auto" onClick={clearSel}>Clear</Button>
        </div>
      )}
      {dancers.length === 0 ? <EmptyState icon={UserPlus} title="No dancers found" /> :
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {dancers.map((d) => (
            <div key={d.id} data-testid={`dancer-card-${d.id}`} className={`bg-white rounded-2xl border p-5 ${d.active === false ? "border-gray-200 opacity-70" : selected.includes(d.id) ? "border-teal ring-1 ring-teal" : "border-gray-100"}`}>
              {isDirector && <label className="flex items-center gap-2 mb-2 text-xs text-muted-foreground cursor-pointer w-fit"><input type="checkbox" data-testid={`select-dancer-${d.id}`} checked={selected.includes(d.id)} onChange={() => toggleSel(d.id)} className="w-4 h-4 accent-teal" />Select</label>}
              <div className="flex items-center gap-3">
                <Avatar first={d.first_name} last={d.last_name} photo={d.photo} size={48} />
                <div className="min-w-0">
                  <p className="font-head font-bold truncate">{d.first_name} {d.last_name}</p>
                  <p className="text-xs text-muted-foreground">Grade {d.grade || "—"} {d.active === false && "· Inactive"}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {d.active === false && <Badge className="bg-gray-100 text-gray-600 rounded-full">Inactive</Badge>}
                {d.nhsda_member && <Badge className="bg-teal-light text-teal-dark rounded-full capitalize">{d.nhsda_member} NHSDA</Badge>}
                <Badge variant="outline" className="rounded-full">{(d.parent_ids || []).length} parent(s)</Badge>
              </div>
              {(() => { const li = logins?.[d.id]; return (
                <div className="mt-3 text-xs flex flex-wrap items-center gap-1.5" data-testid={`dancer-login-info-${d.id}`}>
                  <span className="text-muted-foreground">Login:</span>
                  <span className="font-mono font-semibold">{li?.username || "—"}</span>
                  {li?.activated ? <Badge className="bg-teal-light text-teal-dark rounded-full">Active</Badge>
                    : li?.has_pin ? <Badge className="bg-amber-100 text-amber-700 rounded-full">Card issued</Badge>
                    : li?.setup_sent ? <Badge className="bg-blue-50 text-blue-600 rounded-full">Invited</Badge>
                    : <Badge variant="outline" className="rounded-full text-muted-foreground">No PIN yet</Badge>}
                </div>
              ); })()}
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" className="rounded-full flex-1" data-testid={`edit-dancer-${d.id}`} onClick={() => openEdit(d)}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
                <Button size="sm" variant="outline" className="rounded-full" data-testid={`dancer-access-${d.id}`} onClick={() => openAccess(d)}><IdCard className="w-3.5 h-3.5" /></Button>
                {d.active === false
                  ? <Button size="sm" variant="outline" className="rounded-full" data-testid={`reactivate-dancer-${d.id}`} onClick={() => reactivate(d.id)}><RotateCcw className="w-3.5 h-3.5" /></Button>
                  : <Button size="sm" variant="outline" className="rounded-full" data-testid={`archive-dancer-${d.id}`} onClick={() => archive(d.id)}><Archive className="w-3.5 h-3.5" /></Button>}
                {isDirector && <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50" data-testid={`delete-dancer-${d.id}`} onClick={() => askDelete(d)}><Trash2 className="w-3.5 h-3.5" /></Button>}
              </div>
            </div>
          ))}
        </div>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{editId ? "Edit" : "Add"} Dancer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name"><Input data-testid="dancer-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></Field>
            <Field label="Last name"><Input data-testid="dancer-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></Field>
            <Field label="Preferred name"><Input value={form.preferred_name} onChange={(e) => setForm({ ...form, preferred_name: e.target.value })} /></Field>
            <Field label="Grade"><Input value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} /></Field>
            <Field label="Date of birth"><Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} /></Field>
            <Field label="NHSDA"><select className="w-full border rounded-md h-10 px-3" value={form.nhsda_member} onChange={(e) => setForm({ ...form, nhsda_member: e.target.value })}><option value="">None</option><option value="junior">Junior</option><option value="secondary">Secondary</option></select></Field>
          </div>
          <Field label="Medical / safety alert (restricted)"><Input value={form.medical_alert} onChange={(e) => setForm({ ...form, medical_alert: e.target.value })} /></Field>
          <Field label="Dancer email (optional — used for setup/reset links)"><Input data-testid="dancer-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="dancer@email.com (leave blank for younger dancers)" /></Field>

          <Field label="Classes">
            <div className="flex flex-wrap gap-1.5 mb-2">
              {classIds.length === 0 ? <span className="text-xs text-muted-foreground">No classes yet — search below to enroll.</span> :
                classIds.map((cid) => <span key={cid} data-testid={`dancer-class-chip-${cid}`} className="inline-flex items-center gap-1 rounded-full bg-teal text-white text-xs px-2.5 py-1">{classOf(cid)}<button type="button" onClick={() => toggleClass(cid)} className="ml-0.5">×</button></span>)}
            </div>
            <Input data-testid="dancer-class-search" value={classQ} onChange={(e) => setClassQ(e.target.value)} placeholder="Search classes to add…" />
            {classQ && (
              <div className="mt-1 border rounded-lg divide-y max-h-40 overflow-y-auto">
                {classMatches.length === 0 ? <p className="text-xs text-muted-foreground p-2">No matching classes.</p> :
                  classMatches.map((c) => <button key={c.id} type="button" data-testid={`dancer-class-option-${c.id}`} onClick={() => { toggleClass(c.id); setClassQ(""); }} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50">{c.name}</button>)}
              </div>
            )}
          </Field>

          <Field label="Parents / Guardians">
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(form.parent_ids || []).length === 0 ? <span className="text-xs text-muted-foreground">No parents linked yet.</span> :
                form.parent_ids.map((pid) => { const p = parentOf(pid); return <span key={pid} data-testid={`dancer-parent-chip-${pid}`} className="inline-flex items-center gap-1 rounded-full bg-orange-400 text-white text-xs px-2.5 py-1">{p ? `${p.first_name} ${p.last_name}` : "Parent"}<button type="button" onClick={() => toggleParent(pid)} className="ml-0.5">×</button></span>; })}
            </div>
            <Input data-testid="dancer-parent-search" value={pQ} onChange={(e) => setPQ(e.target.value)} placeholder="Search parents by name, email or phone…" />
            {pQ.trim() && (
              <div className="mt-1 border rounded-lg divide-y max-h-48 overflow-y-auto">
                {parentMatches.length === 0 ? <p className="text-xs text-muted-foreground p-2">No matching parents.</p> :
                  parentMatches.map((p) => (
                    <button key={p.id} type="button" data-testid={`dancer-parent-option-${p.id}`} onClick={() => { toggleParent(p.id); setPQ(""); }} className="w-full text-left px-3 py-2 hover:bg-gray-50">
                      <span className="text-sm font-semibold">{p.first_name} {p.last_name}</span>
                      <span className="text-xs text-muted-foreground"> · {p.email || p.phone || "no contact"}</span>
                      {(p.dancer_ids || []).length > 0 && <span className="block text-xs text-muted-foreground">Linked: {p.dancer_ids.map(nameOf).filter(Boolean).join(", ")}</span>}
                    </button>
                  ))}
              </div>
            )}
            {addingParent ? (
              <div className="mt-2 border rounded-lg p-3 space-y-2 bg-gray-50">
                <div className="grid grid-cols-2 gap-2">
                  <Input data-testid="inline-parent-first" value={newParent.first_name} onChange={(e) => setNewParent({ ...newParent, first_name: e.target.value })} placeholder="First name" />
                  <Input data-testid="inline-parent-last" value={newParent.last_name} onChange={(e) => setNewParent({ ...newParent, last_name: e.target.value })} placeholder="Last name" />
                  <Input data-testid="inline-parent-email" value={newParent.email} onChange={(e) => setNewParent({ ...newParent, email: e.target.value })} placeholder="Email" />
                  <Input data-testid="inline-parent-phone" value={newParent.phone} onChange={(e) => setNewParent({ ...newParent, phone: e.target.value })} placeholder="Phone" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="rounded-full bg-teal hover:bg-teal-dark" data-testid="inline-parent-save" onClick={addParentInline}>Add & link</Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => setAddingParent(false)}>Cancel</Button>
                </div>
              </div>
            ) : <button type="button" data-testid="inline-add-parent-toggle" onClick={() => setAddingParent(true)} className="text-sm text-teal-dark font-semibold mt-1">+ Can't find them? Add a new parent</button>}
          </Field>

          <Field label="Private Director notes"><Textarea value={form.director_notes} onChange={(e) => setForm({ ...form, director_notes: e.target.value })} /></Field>
          <DialogFooter><Button data-testid="save-dancer-button" onClick={save} className="rounded-full bg-teal hover:bg-teal-dark">Save Dancer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-head">Delete {delTarget?.first_name} {delTarget?.last_name}?</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <p className="text-muted-foreground">This permanently removes the dancer. Parents and siblings are kept.</p>
            {impact ? <div className="rounded-xl bg-gray-50 border p-3 space-y-1" data-testid="dancer-impact">
              <p><strong>{impact.enrollments.length}</strong> class enrollment(s){impact.enrollments.length ? `: ${impact.enrollments.join(", ")}` : ""} will be removed.</p>
              <p><strong>{impact.parents.length}</strong> parent link(s) detached{impact.parents.length ? ` (${impact.parents.join(", ")})` : ""} — parents are NOT deleted.</p>
              {impact.casting_count > 0 && <p><strong>{impact.casting_count}</strong> production casting record(s) reference this dancer.</p>}
            </div> : <p className="text-xs text-muted-foreground">Loading impact…</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDelTarget(null)}>Cancel</Button>
            <Button className="rounded-full bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-dancer" onClick={doDelete}>Delete permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-head">Export roster to Excel</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">Download a workbook with <strong>Students</strong> and <strong>Parents</strong> tabs — the same format the importer reads, so you can edit and re-import.</p>
            <Field label="Scope">
              <select data-testid="export-class-select" className="w-full border rounded-md h-10 px-3" value={exportClass} onChange={(e) => setExportClass(e.target.value)}>
                <option value="">All active dancers</option>
                {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setExportOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-export-button" className="rounded-full bg-teal hover:bg-teal-dark" onClick={doExport}><Download className="w-4 h-4 mr-1" />Download .xlsx</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent className="bg-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">Reassign {selDancers.length} dancer(s)</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-muted-foreground">{selDancers.map((d) => `${d.first_name} ${d.last_name}`).join(", ")}</p>
            <div className="flex gap-2">
              {["copy", "move"].map((m) => (
                <button key={m} type="button" data-testid={`reassign-mode-${m}`} onClick={() => setReassign((r) => ({ ...r, mode: m }))} className={`flex-1 rounded-full h-9 text-sm font-semibold capitalize border ${reassign.mode === m ? "bg-teal text-white border-teal" : "bg-white"}`}>{m}</button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{reassign.mode === "copy" ? "Adds the dancers to the classes below (keeps current classes)." : "Adds to the target classes and removes them from the class you pick below."}</p>
            {reassign.mode === "move" && (
              <Field label="Move out of class">
                <select data-testid="reassign-from-class" className="w-full border rounded-md h-10 px-3" value={reassign.from_class_id} onChange={(e) => setReassign((r) => ({ ...r, from_class_id: e.target.value }))}>
                  <option value="">— choose class —</option>
                  {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
            )}
            <Field label={reassign.mode === "move" ? "Move into classes" : "Copy into classes"}>
              <div className="border rounded-lg divide-y max-h-52 overflow-y-auto">
                {classes.filter((c) => c.active !== false).map((c) => (
                  <label key={c.id} data-testid={`reassign-class-${c.id}`} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50">
                    <input type="checkbox" className="w-4 h-4 accent-teal" checked={reassign.to_class_ids.includes(c.id)} onChange={() => toggleReClass(c.id)} />
                    <span>{c.name}</span>
                  </label>
                ))}
              </div>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setReassignOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-reassign-button" disabled={busy} className="rounded-full bg-teal hover:bg-teal-dark" onClick={doReassign}>{busy ? "Working…" : "Apply"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-head">Merge duplicate dancers</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">Choose which record to <strong>keep</strong>. The other's classes, parent links, casting and progress move onto it, then the duplicate is permanently deleted.</p>
            <div className="space-y-2">
              {selDancers.map((d) => (
                <label key={d.id} data-testid={`merge-keep-${d.id}`} className={`flex items-center gap-3 border rounded-xl p-3 cursor-pointer ${mergeKeep === d.id ? "border-teal ring-1 ring-teal" : ""}`}>
                  <input type="radio" name="merge-keep" className="w-4 h-4 accent-teal" checked={mergeKeep === d.id} onChange={() => setMergeKeep(d.id)} />
                  <div>
                    <p className="font-semibold">{d.first_name} {d.last_name}</p>
                    <p className="text-xs text-muted-foreground">Grade {d.grade || "—"} · {(d.parent_ids || []).length} parent(s){mergeKeep === d.id ? " · KEEP" : " · will be deleted"}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setMergeOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-merge-button" disabled={busy} className="rounded-full bg-red-600 hover:bg-red-700 text-white" onClick={doMerge}>{busy ? "Merging…" : "Merge & delete duplicate"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!accessDlg} onOpenChange={(o) => !o && setAccessDlg(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-head">Login & access — {accessDlg?.first_name} {accessDlg?.last_name}</DialogTitle></DialogHeader>
          {accessDlg && (() => { const li = logins?.[accessDlg.id]; const dParents = (accessDlg.parent_ids || []).map((pid) => parents.find((p) => p.id === pid)).filter(Boolean); return (
            <div className="space-y-4 text-sm">
              <div className="rounded-xl bg-gray-50 border p-3 flex items-center justify-between">
                <div><p className="text-xs text-muted-foreground">Username</p><p className="font-mono font-bold">{li?.username || "—"}</p></div>
                {li?.activated ? <Badge className="bg-teal-light text-teal-dark rounded-full">Active account</Badge>
                  : li?.has_pin ? <Badge className="bg-amber-100 text-amber-700 rounded-full">Card issued (not yet used)</Badge>
                  : li?.setup_sent ? <Badge className="bg-blue-50 text-blue-600 rounded-full">Invitation sent</Badge>
                  : <Badge variant="outline" className="rounded-full">No PIN set yet</Badge>}
              </div>
              <p className="text-xs text-muted-foreground">Studio V never shows an existing PIN. Generate a new login card or send a setup link so the dancer can choose their own PIN.</p>

              <div className="space-y-2">
                <p className="font-semibold">Printable login card</p>
                <Button size="sm" data-testid="access-gen-card" className="rounded-full bg-teal hover:bg-teal-dark" onClick={() => genCard(accessDlg)}><IdCard className="w-3.5 h-3.5 mr-1" />{li?.has_pin ? "Regenerate card (new PIN)" : "Generate login card"}</Button>
                <p className="text-xs text-muted-foreground">Creates a one-time temporary PIN (shown once). Regenerating invalidates the old card.</p>
              </div>

              <div className="space-y-2 border-t pt-3">
                <p className="font-semibold">Email a setup link</p>
                <div className="flex gap-2">
                  {["dancer", "parent"].map((r) => (
                    <button key={r} type="button" data-testid={`access-recipient-${r}`} onClick={() => setSetupRecipient(r)} className={`flex-1 rounded-full h-9 text-sm font-semibold capitalize border ${setupRecipient === r ? "bg-teal text-white border-teal" : "bg-white"}`}>{r === "dancer" ? "Dancer's email" : "A parent"}</button>
                  ))}
                </div>
                {setupRecipient === "dancer" ? (
                  <p className="text-xs text-muted-foreground">{accessDlg.email ? <>Sends to <strong>{accessDlg.email}</strong></> : "This dancer has no email — choose a parent instead."}</p>
                ) : (
                  <select data-testid="access-parent-select" className="w-full border rounded-md h-10 px-3" value={setupParent} onChange={(e) => setSetupParent(e.target.value)}>
                    <option value="">— choose a parent —</option>
                    {dParents.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}{p.email ? ` · ${p.email}` : " · (no email)"}</option>)}
                  </select>
                )}
                <Button size="sm" variant="outline" data-testid="access-send-setup" className="rounded-full" onClick={() => sendSetup(accessDlg)}><Send className="w-3.5 h-3.5 mr-1" />Send setup link</Button>
              </div>

              <button type="button" onClick={() => { setAccessDlg(null); openLogin(accessDlg); }} className="text-xs text-teal-dark font-semibold">Advanced: set username & PIN manually</button>
            </div>
          ); })()}
        </DialogContent>
      </Dialog>

      <Dialog open={cardsDlg.open} onOpenChange={(o) => setCardsDlg((v) => ({ ...v, open: o }))}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bulk-cards-dialog">
          <DialogHeader><DialogTitle className="font-head">Print login cards</DialogTitle></DialogHeader>
          {(() => {
            const items = cardsDlg.items;
            const newCount = items.filter((i) => !i.activated || cardsDlg.replace.includes(i.dancer_id)).length;
            const unameOnly = items.length - newCount;
            return (
              <div className="space-y-4 text-sm">
                <div>
                  <Label>Who to print for</Label>
                  <div className="flex gap-2 mt-1.5">
                    {[["selected", `Selected (${selected.length})`], ["class", "A class"], ["all", "All active"]].map(([val, lbl]) => (
                      <button key={val} type="button" data-testid={`cards-scope-${val}`} onClick={() => cardsScope(val)} className={`flex-1 rounded-full h-9 text-xs font-semibold border ${cardsDlg.scope === val ? "bg-teal text-white border-teal" : "bg-white"}`}>{lbl}</button>
                    ))}
                  </div>
                </div>
                {cardsDlg.scope === "class" && (
                  <select data-testid="cards-class-select" className="w-full border rounded-md h-10 px-3" value={cardsDlg.classId} onChange={(e) => cardsClass(e.target.value)}>
                    <option value="">— choose a class —</option>
                    {classes.filter((c) => c.active !== false).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                )}

                {!cardsDlg.cards ? (
                  <>
                    <p className="text-xs" data-testid="cards-preview-count"><strong>{items.length}</strong> dancer(s) • <strong>{newCount}</strong> will get a new temp PIN • <strong>{unameOnly}</strong> username-only</p>
                    {items.some((i) => i.activated) && (
                      <div className="border rounded-xl p-3 bg-amber-50/60">
                        <p className="text-xs font-semibold text-amber-700 mb-2">Already-active logins (PIN kept private). Tick to issue a replacement PIN (invalidates the old one):</p>
                        <div className="space-y-1.5 max-h-40 overflow-y-auto">
                          {items.filter((i) => i.activated).map((i) => (
                            <label key={i.dancer_id} data-testid={`cards-replace-${i.dancer_id}`} className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" className="w-4 h-4 accent-teal" checked={cardsDlg.replace.includes(i.dancer_id)} onChange={() => toggleReplace(i.dancer_id)} />
                              <span>{i.name} <span className="text-muted-foreground font-mono text-xs">{i.username}</span></span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="max-h-40 overflow-y-auto border rounded-xl divide-y">
                      {items.length === 0 ? <p className="text-xs text-muted-foreground p-3">No dancers in this selection yet.</p> :
                        items.map((i) => (
                          <div key={i.dancer_id} className="flex items-center justify-between px-3 py-2 text-xs">
                            <span>{i.name} <span className="font-mono text-muted-foreground">{i.username}</span></span>
                            {i.activated && !cardsDlg.replace.includes(i.dancer_id) ? <Badge variant="outline" className="rounded-full">username only</Badge> : <Badge className="bg-teal-light text-teal-dark rounded-full">new PIN</Badge>}
                          </div>
                        ))}
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2">New PINs are shown once. Download or print now.</p>
                    <div className="max-h-56 overflow-y-auto border rounded-xl divide-y" data-testid="cards-result-list">
                      {cardsDlg.cards.map((c) => (
                        <div key={c.dancer_id} className="flex items-center justify-between px-3 py-2">
                          <div><p className="font-semibold text-sm">{c.name}</p><p className="text-xs text-muted-foreground font-mono">{c.username}</p></div>
                          <div className="text-right">{c.pin ? <p className="font-mono font-bold tracking-widest">{c.pin}</p> : <span className="text-xs text-muted-foreground">own PIN</span>}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setCardsDlg((v) => ({ ...v, open: false }))}>Close</Button>
            {!cardsDlg.cards
              ? <Button data-testid="cards-generate-button" disabled={cardsDlg.items.length === 0} className="rounded-full bg-teal hover:bg-teal-dark" onClick={generateCards}>Generate {cardsDlg.items.length} card(s)</Button>
              : <>
                  <Button variant="outline" data-testid="cards-print-html-button" className="rounded-full" onClick={() => printCards(cardsDlg.cards)}><Printer className="w-4 h-4 mr-1" />Print</Button>
                  <Button data-testid="cards-download-pdf-button" className="rounded-full bg-teal hover:bg-teal-dark" onClick={downloadCardsPdf}><Download className="w-4 h-4 mr-1" />Download PDF</Button>
                </>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cardData} onOpenChange={(o) => !o && setCardData(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-head">Login card(s) — PIN shown once</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm" data-testid="card-result">
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2">Write these down or print now — for security the PIN will not be shown again. Each dancer chooses their own PIN at first sign-in.</p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {(cardData?.cards || []).map((c) => (
                <div key={c.dancer_id} className="border rounded-xl p-3 flex items-center justify-between">
                  <div><p className="font-semibold">{c.name}</p><p className="text-xs text-muted-foreground">Username <span className="font-mono">{c.username}</span></p></div>
                  <div className="text-right"><p className="text-xs text-muted-foreground">Temp PIN</p><p className="font-mono font-bold tracking-widest text-lg">{c.pin}</p></div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setCardData(null)}>Close</Button>
            <Button data-testid="print-cards-button" className="rounded-full bg-teal hover:bg-teal-dark" onClick={() => printCards(cardData.cards)}><Printer className="w-4 h-4 mr-1" />Print cards</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={loginDlg} onOpenChange={setLoginDlg}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-head">Dancer Login — {loginForm.dancer?.first_name} {loginForm.dancer?.last_name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Dancers sign in with a username + PIN. No email required. This links to the existing profile.</p>
          <Field label="Username"><Input data-testid="dancer-login-username" value={loginForm.username} onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })} placeholder="e.g. emma" /></Field>
          <Field label="PIN (min 4 digits)"><Input data-testid="dancer-login-pin" value={loginForm.pin} onChange={(e) => setLoginForm({ ...loginForm, pin: e.target.value })} placeholder="e.g. 1234" /></Field>
          <DialogFooter><Button data-testid="save-dancer-login-button" onClick={saveLogin} className="rounded-full bg-teal hover:bg-teal-dark">Save Login</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Teachers ---------------- */
function TeacherSection({ teachers, onChange }) {
  const empty = { first_name: "", last_name: "", preferred_name: "", email: "", phone: "", bio: "", start_date: "", active: true, styles_taught: [], director_notes: "", permissions: {} };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const save = async () => {
    try {
      if (editId) await api.put(`/people/teachers/${editId}`, form); else await api.post("/people/teachers", form);
      toast.success("Teacher saved"); setOpen(false); setForm(empty); setEditId(null); onChange();
    } catch (e) { toast.error("Error"); }
  };
  const invite = async (t) => {
    try { const r = await api.post(`/people/teachers/${t.id}/invite`); toast.success(`Invite sent to ${r.data.to}`); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send invite"); }
  };
  return (
    <div>
      <div className="flex justify-end mb-4"><Button data-testid="add-teacher-button" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} className="rounded-full bg-teal hover:bg-teal-dark"><UserPlus className="w-4 h-4 mr-2" />Add Teacher</Button></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {teachers.map((t) => (
          <div key={t.id} data-testid={`teacher-card-${t.id}`} className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center gap-3">
              <Avatar first={t.first_name} last={t.last_name} photo={t.photo} size={48} color="#000000" />
              <div><p className="font-head font-bold">{t.preferred_name || `${t.first_name} ${t.last_name}`}</p><p className="text-xs text-muted-foreground">{t.email}</p></div>
            </div>
            <p className="text-sm mt-3 text-muted-foreground line-clamp-2">{t.bio}</p>
            <div className="flex flex-wrap gap-1.5 mt-3">{(t.styles_taught || []).map((s) => <Badge key={s} variant="outline" className="rounded-full">{s}</Badge>)}</div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="outline" className="rounded-full flex-1" data-testid={`edit-teacher-${t.id}`} onClick={() => { setForm({ ...empty, ...t }); setEditId(t.id); setOpen(true); }}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
              {t.email && t.active !== false && <Button size="sm" variant="outline" className="rounded-full" data-testid={`invite-teacher-${t.id}`} onClick={() => invite(t)} title="Send login invite"><Send className="w-3.5 h-3.5" /></Button>}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{editId ? "Edit" : "Add"} Teacher</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name"><Input data-testid="teacher-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></Field>
            <Field label="Last name"><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></Field>
            <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          </div>
          <Field label="Bio"><Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></Field>
          <Field label="Styles taught (comma separated)"><Input value={(form.styles_taught || []).join(", ")} onChange={(e) => setForm({ ...form, styles_taught: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></Field>
          <DialogFooter><Button data-testid="save-teacher-button" onClick={save} className="rounded-full bg-teal hover:bg-teal-dark">Save Teacher</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Parents ---------------- */
function ParentSection({ parents, dancers, isDirector, onChange }) {
  const empty = { first_name: "", last_name: "", email: "", phone: "", relationship: "", comm_method: "email", emergency_contact: false, authorized_pickup: false, dancer_ids: [], active: true, director_notes: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const save = async () => {
    try {
      if (editId) await api.put(`/people/parents/${editId}`, form); else await api.post("/people/parents", form);
      toast.success("Parent saved"); setOpen(false); setForm(empty); setEditId(null); onChange();
    } catch (e) { toast.error("Error"); }
  };
  const archive = async (id) => { await api.post(`/people/parents/${id}/archive`); toast.success("Marked inactive"); onChange(); };
  const reactivate = async (id) => { await api.post(`/people/parents/${id}/reactivate`); toast.success("Reactivated"); onChange(); };
  const invite = async (p) => {
    try { const r = await api.post(`/people/parents/${p.id}/invite`); toast.success(`Invite sent to ${r.data.to}`); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send invite"); }
  };
  const [delTarget, setDelTarget] = useState(null);
  const [impact, setImpact] = useState(null);
  const askDelete = async (p) => { setDelTarget(p); setImpact(null); try { const r = await api.get(`/people/parents/${p.id}/impact`); setImpact(r.data); } catch { /* ignore */ } };
  const doDelete = async () => { try { await api.delete(`/people/parents/${delTarget.id}`); toast.success("Parent deleted"); setDelTarget(null); onChange(); } catch { toast.error("Error"); } };
  const [selected, setSelected] = useState([]);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeKeep, setMergeKeep] = useState("");
  const [busy, setBusy] = useState(false);
  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const selParents = parents.filter((p) => selected.includes(p.id));
  const undo = async (uid) => {
    if (!uid) return;
    try { await api.post(`/people/undo/${uid}`); toast.success("Reverted"); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not undo"); }
  };
  const openMerge = () => { setMergeKeep(selected[0] || ""); setMergeOpen(true); };
  const doMerge = async () => {
    const keep = mergeKeep; const remove = selected.find((x) => x !== keep);
    if (!keep || !remove) { toast.error("Select exactly two parents"); return; }
    setBusy(true);
    try { const rm = await api.post("/people/parents/merge", { keep_id: keep, remove_id: remove }); const uid = rm.data.undo_id; toast.success("Parents merged", uid ? { action: { label: "Undo", onClick: () => undo(uid) }, duration: 12000 } : undefined); setMergeOpen(false); setSelected([]); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };
  return (
    <div>
      <div className="flex justify-end mb-4"><Button data-testid="add-parent-button" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} className="rounded-full bg-teal hover:bg-teal-dark"><UserPlus className="w-4 h-4 mr-2" />Add Parent</Button></div>
      {isDirector && selected.length > 0 && (
        <div data-testid="parent-bulk-bar" className="flex flex-wrap items-center gap-2 mb-4 bg-orange-50 border border-orange-200 rounded-2xl px-4 py-3">
          <span className="text-sm font-semibold text-orange-700">{selected.length} selected</span>
          <Button size="sm" variant="outline" data-testid="parent-merge-button" disabled={selected.length !== 2} className="rounded-full ml-2" onClick={openMerge}><GitMerge className="w-3.5 h-3.5 mr-1" />Merge duplicates</Button>
          {selected.length !== 2 && <span className="text-xs text-muted-foreground">Select exactly 2 to merge</span>}
          <Button size="sm" variant="ghost" className="rounded-full ml-auto" onClick={() => setSelected([])}>Clear</Button>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {parents.map((p) => (
          <div key={p.id} data-testid={`parent-card-${p.id}`} className={`bg-white rounded-2xl border p-5 ${p.active === false ? "border-gray-200 opacity-70" : selected.includes(p.id) ? "border-teal ring-1 ring-teal" : "border-gray-100"}`}>
            {isDirector && <label className="flex items-center gap-2 mb-2 text-xs text-muted-foreground cursor-pointer w-fit"><input type="checkbox" data-testid={`select-parent-${p.id}`} checked={selected.includes(p.id)} onChange={() => toggleSel(p.id)} className="w-4 h-4 accent-teal" />Select</label>}
            <div className="flex items-center gap-3"><Avatar first={p.first_name} last={p.last_name} size={48} color="#FB923C" />
              <div><p className="font-head font-bold">{p.first_name} {p.last_name}</p><p className="text-xs text-muted-foreground">{p.relationship}</p></div></div>
            <p className="text-sm mt-3">{p.email || p.phone}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {p.active === false && <Badge className="bg-gray-100 text-gray-600 rounded-full">Inactive</Badge>}
              {p.emergency_contact && <Badge className="bg-red-50 text-red-600 rounded-full">Emergency</Badge>}
              {p.authorized_pickup && <Badge className="bg-teal-light text-teal-dark rounded-full">Pickup OK</Badge>}
              <Badge variant="outline" className="rounded-full">{(p.dancer_ids || []).length} dancer(s)</Badge>
            </div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="outline" className="rounded-full flex-1" data-testid={`edit-parent-${p.id}`} onClick={() => { setForm({ ...empty, ...p }); setEditId(p.id); setOpen(true); }}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
              {isDirector && p.active !== false && p.email && <Button size="sm" variant="outline" className="rounded-full" data-testid={`invite-parent-${p.id}`} onClick={() => invite(p)} title="Send login invite"><Send className="w-3.5 h-3.5" /></Button>}
              {p.active === false
                ? <Button size="sm" variant="outline" className="rounded-full" data-testid={`reactivate-parent-${p.id}`} onClick={() => reactivate(p.id)}><RotateCcw className="w-3.5 h-3.5" /></Button>
                : <Button size="sm" variant="outline" className="rounded-full" data-testid={`archive-parent-${p.id}`} onClick={() => archive(p.id)}><Archive className="w-3.5 h-3.5" /></Button>}
              {isDirector && <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50" data-testid={`delete-parent-${p.id}`} onClick={() => askDelete(p)}><Trash2 className="w-3.5 h-3.5" /></Button>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-head">Merge duplicate parents</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">Choose which record to <strong>keep</strong>. The other's dancer links move onto it, then the duplicate is permanently deleted.</p>
            <div className="space-y-2">
              {selParents.map((p) => (
                <label key={p.id} data-testid={`parent-merge-keep-${p.id}`} className={`flex items-center gap-3 border rounded-xl p-3 cursor-pointer ${mergeKeep === p.id ? "border-teal ring-1 ring-teal" : ""}`}>
                  <input type="radio" name="parent-merge-keep" className="w-4 h-4 accent-teal" checked={mergeKeep === p.id} onChange={() => setMergeKeep(p.id)} />
                  <div>
                    <p className="font-semibold">{p.first_name} {p.last_name}</p>
                    <p className="text-xs text-muted-foreground">{p.email || p.phone || "no contact"} · {(p.dancer_ids || []).length} dancer(s){mergeKeep === p.id ? " · KEEP" : " · will be deleted"}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setMergeOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-parent-merge-button" disabled={busy} className="rounded-full bg-red-600 hover:bg-red-700 text-white" onClick={doMerge}>{busy ? "Merging…" : "Merge & delete duplicate"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-head">Delete {delTarget?.first_name} {delTarget?.last_name}?</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <p className="text-muted-foreground">This permanently removes the parent. Their dancers and any siblings are kept — only the parent links are detached.</p>
            {impact ? <div className="rounded-xl bg-gray-50 border p-3" data-testid="parent-impact">
              <p><strong>{impact.dancers.length}</strong> linked dancer(s){impact.dancers.length ? `: ${impact.dancers.join(", ")}` : ""} will be detached (not deleted).</p>
            </div> : <p className="text-xs text-muted-foreground">Loading impact…</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDelTarget(null)}>Cancel</Button>
            <Button className="rounded-full bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-parent" onClick={doDelete}>Delete permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{editId ? "Edit" : "Add"} Parent</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name"><Input data-testid="parent-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></Field>
            <Field label="Last name"><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></Field>
            <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Relationship"><Input value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} /></Field>
          </div>
          <div className="flex gap-6 mt-2">
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.emergency_contact} onCheckedChange={(v) => setForm({ ...form, emergency_contact: v })} />Emergency contact</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.authorized_pickup} onCheckedChange={(v) => setForm({ ...form, authorized_pickup: v })} />Authorized pickup</label>
          </div>
          <Field label="Connected dancers"><div className="flex flex-wrap gap-2">{dancers.map((d) => {
            const on = form.dancer_ids?.includes(d.id);
            return <button key={d.id} type="button" onClick={() => setForm({ ...form, dancer_ids: on ? form.dancer_ids.filter((x) => x !== d.id) : [...(form.dancer_ids || []), d.id] })}
              className={`px-3 py-1 rounded-full text-sm border ${on ? "bg-teal text-white border-teal" : "bg-white"}`}>{d.first_name} {d.last_name}</button>;
          })}</div></Field>
          <DialogFooter><Button data-testid="save-parent-button" onClick={save} className="rounded-full bg-teal hover:bg-teal-dark">Save Parent</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Families (derived from dancer↔parent links) ---------------- */
function FamilyView({ dancers, parents, onChange }) {
  const [fq, setFq] = useState("");
  const activeD = dancers.filter((d) => d.active !== false);
  const activeP = parents.filter((p) => p.active !== false);

  const groups = useMemo(() => {
    const dMap = Object.fromEntries(activeD.map((d) => [d.id, d]));
    const pMap = Object.fromEntries(activeP.map((p) => [p.id, p]));
    const uf = {};
    const find = (x) => { if (uf[x] === undefined) uf[x] = x; if (uf[x] !== x) uf[x] = find(uf[x]); return uf[x]; };
    const union = (a, b) => { uf[find(a)] = find(b); };
    activeD.forEach((d) => { find("d:" + d.id); (d.parent_ids || []).forEach((pid) => { if (pMap[pid]) union("d:" + d.id, "p:" + pid); }); });
    activeP.forEach((p) => { find("p:" + p.id); (p.dancer_ids || []).forEach((did) => { if (dMap[did]) union("p:" + p.id, "d:" + did); }); });
    const buckets = {};
    Object.keys(uf).forEach((k) => { const r = find(k); (buckets[r] = buckets[r] || { parents: [], dancers: [] }); if (k.startsWith("d:")) buckets[r].dancers.push(dMap[k.slice(2)]); else buckets[r].parents.push(pMap[k.slice(2)]); });
    let arr = Object.values(buckets).filter((g) => g.dancers.length);
    arr.forEach((g) => { g.dancers.sort((a, b) => (a.last_name || "").localeCompare(b.last_name || "")); });
    arr.sort((a, b) => (a.dancers[0]?.last_name || "").localeCompare(b.dancers[0]?.last_name || ""));
    return arr;
  }, [activeD, activeP]);

  const shown = groups.filter((g) => {
    if (!fq.trim()) return true;
    const hay = [...g.dancers, ...g.parents].map((x) => `${x.first_name} ${x.last_name} ${x.email || ""}`).join(" ").toLowerCase();
    return hay.includes(fq.toLowerCase());
  });

  const unlink = async (dancer, pid) => {
    try { await api.put(`/people/dancers/${dancer.id}`, { ...dancer, parent_ids: (dancer.parent_ids || []).filter((x) => x !== pid) }); toast.success("Unlinked"); onChange(); }
    catch { toast.error("Error"); }
  };
  const link = async (dancer, pid) => {
    if (!pid) return;
    try { await api.post("/people/connect", { parent_id: pid, dancer_id: dancer.id }); toast.success("Linked"); onChange(); }
    catch { toast.error("Error"); }
  };
  const pName = (id) => { const p = activeP.find((x) => x.id === id); return p ? `${p.first_name} ${p.last_name}` : ""; };

  return (
    <div>
      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="family-search" value={fq} onChange={(e) => setFq(e.target.value)} placeholder="Search siblings or parents…" className="pl-9 rounded-full bg-white" />
      </div>
      <p className="text-xs text-muted-foreground mb-3">Households are built automatically from dancer↔parent links — no separate family record needed.</p>
      {shown.length === 0 ? <EmptyState icon={Users} title="No families found" hint="Link parents to dancers from a dancer's Edit form." /> :
        <div className="grid md:grid-cols-2 gap-4">
          {shown.map((g, i) => (
            <div key={i} data-testid="family-group" className="bg-white rounded-2xl border border-gray-100 p-5">
              <p className="font-head font-bold text-lg flex items-center gap-2"><Users className="w-4 h-4 text-teal" />{g.parents.map((p) => p.last_name).filter(Boolean)[0] || g.dancers[0]?.last_name || "Household"} Household</p>
              <div className="mt-2">
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Parents</p>
                {g.parents.length === 0 ? <p className="text-sm text-muted-foreground">No parents linked yet.</p> :
                  <div className="flex flex-wrap gap-1.5 mt-1">{g.parents.map((p) => <span key={p.id} className="rounded-full bg-orange-400 text-white text-xs px-2.5 py-1">{p.first_name} {p.last_name}{p.email ? ` · ${p.email}` : ""}</span>)}</div>}
              </div>
              <div className="mt-3 space-y-2">
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Siblings</p>
                {g.dancers.map((d) => (
                  <div key={d.id} data-testid={`family-dancer-${d.id}`} className="border-b border-gray-50 pb-2">
                    <p className="text-sm font-semibold">{d.first_name} {d.last_name}</p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      {(d.parent_ids || []).filter((pid) => activeP.some((p) => p.id === pid)).map((pid) => (
                        <span key={pid} className="inline-flex items-center gap-1 rounded-full bg-teal-light text-teal-dark text-xs px-2 py-0.5">{pName(pid)}<button type="button" data-testid={`unlink-${d.id}-${pid}`} onClick={() => unlink(d, pid)} className="ml-0.5">×</button></span>
                      ))}
                      <select data-testid={`link-parent-${d.id}`} className="border rounded-full h-7 px-2 text-xs bg-white" value="" onChange={(e) => link(d, e.target.value)}>
                        <option value="">+ link parent…</option>
                        {activeP.filter((p) => !(d.parent_ids || []).includes(p.id)).map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>}
    </div>
  );
}

/* ---------------- Accounts ---------------- */
function AccountSection({ accounts, dancers, teachers, parents, onChange }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", role: "dancer", profile_id: "", name: "" });
  const [peOpen, setPeOpen] = useState(false);
  const [peRecips, setPeRecips] = useState([]);
  const [peResult, setPeResult] = useState(null);
  const openPe = async () => { setPeResult(null); try { const r = await api.get("/people/parent-login-email/preview"); setPeRecips(r.data.recipients); setPeOpen(true); } catch { toast.error("Error loading recipients"); } };
  const sendPe = async (pid) => { try { const r = await api.post("/people/parent-login-email/send", pid ? { parent_id: pid } : {}); setPeResult(r.data); toast.success(`Sent ${r.data.count}${r.data.failed.length ? `, ${r.data.failed.length} failed` : ""}`); } catch { toast.error("Send failed"); } };
  const profilesFor = (role) => role === "dancer" ? dancers : role === "teacher" ? teachers : role === "parent" ? parents : [];
  const save = async () => {
    try {
      const res = await api.post("/people/accounts/link", form);
      if (res.data.duplicate_warning) toast.warning("Existing account updated (no duplicate created).");
      else toast.success("Login created & linked");
      setOpen(false); setForm({ email: "", role: "dancer", profile_id: "", name: "" }); onChange();
    } catch (e) { toast.error("Error"); }
  };
  return (
    <div>
      <div className="flex justify-end gap-2 mb-4">
        <Button data-testid="send-parent-login-email" variant="outline" onClick={openPe} className="rounded-full"><Send className="w-4 h-4 mr-2" />Send Parent Login Email</Button>
        <Button data-testid="invite-user-button" onClick={() => setOpen(true)} className="rounded-full bg-teal hover:bg-teal-dark"><Link2 className="w-4 h-4 mr-2" />Create / Link Login</Button>
      </div>
      <Dialog open={peOpen} onOpenChange={setPeOpen}><DialogContent className="bg-white max-w-lg">
        <DialogHeader><DialogTitle className="font-head">Send Parent Login Email</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Active parents without Google sign-in. The email helps them access their EXISTING account (no new account is created).</p>
        <div className="max-h-72 overflow-auto divide-y mt-1">
          {peRecips.map((r) => <div key={r.parent_id} data-testid={`pe-recip-${r.parent_id}`} className="flex items-center justify-between py-2 text-sm gap-3">
            <div className="min-w-0"><p className="font-semibold">{r.name}</p><p className="text-xs text-muted-foreground truncate">{r.email} · {r.has_password ? "has password" : "needs password"}{peResult?.sent?.includes(r.email) ? " · ✓ sent" : peResult?.failed?.includes(r.email) ? " · ✗ failed" : ""}</p></div>
            <Button size="sm" variant="outline" className="rounded-full" data-testid={`pe-send-${r.parent_id}`} onClick={() => sendPe(r.parent_id)}>Send</Button>
          </div>)}
          {!peRecips.length && <p className="text-sm text-muted-foreground py-3">No eligible parents (all are Google-linked or have no email).</p>}
        </div>
        <div className="flex justify-end pt-2"><Button data-testid="pe-send-all" className="rounded-full bg-teal hover:bg-teal-dark" onClick={() => sendPe(null)}>Send to all ({peRecips.length})</Button></div>
      </DialogContent></Dialog>
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {accounts.map((a) => {
          const status = a.has_credential || a.activated_at ? { t: "Active", c: "bg-green-50 text-green-700" } : a.invited_at ? { t: "Invited", c: "bg-amber-50 text-amber-700" } : { t: "No login", c: "bg-gray-100 text-gray-500" };
          return (
          <div key={a.user_id} data-testid={`account-row-${a.user_id}`} className="flex items-center justify-between px-5 py-3.5 border-b last:border-0">
            <div><p className="font-semibold">{a.name}</p><p className="text-xs text-muted-foreground">{a.email || (a.username ? `@${a.username}` : "")}</p></div>
            <div className="flex items-center gap-2">
              <Badge className={`rounded-full ${status.c}`} data-testid={`account-status-${a.user_id}`}>{status.t}</Badge>
              <Badge className="bg-teal-light text-teal-dark rounded-full capitalize">{a.role}</Badge>
            </div>
          </div>
          );
        })}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-head">Create / Link Login</DialogTitle></DialogHeader>
          <Field label="Email"><Input data-testid="account-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="person@email.com" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Role"><select className="w-full border rounded-md h-10 px-3" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value, profile_id: "" })}><option value="dancer">Dancer</option><option value="teacher">Teacher</option><option value="parent">Parent</option><option value="director">Director</option></select></Field>
            <Field label="Link to profile"><select data-testid="account-profile" className="w-full border rounded-md h-10 px-3" value={form.profile_id} onChange={(e) => setForm({ ...form, profile_id: e.target.value })}><option value="">— none —</option>{profilesFor(form.role).map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}</select></Field>
          </div>
          <DialogFooter><Button data-testid="save-account-button" onClick={save} className="rounded-full bg-teal hover:bg-teal-dark">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
